import type { AudioSource } from 'expo-audio'
import * as FileSystem from 'expo-file-system/legacy'
import { z } from 'zod'

import type { MeasurementDTO } from '../api/models'
import { MeasurementApi } from '../api'
import { MeasurementFromJSON } from '../api/models'
import { getApiConfig } from './api-config'
import { nullableStringSchema } from './dal-utils'

export const measurementSummarySchema = z.object({
  id: z.number().min(0),
  definition: z.object({
    id: z.number().min(0),
    name: z.string().min(1),
  }),
  sessionId: z.number().min(0),
  recording: nullableStringSchema
    .optional()
    .transform((value) => value ?? null),
})

const measurementQuestionSchema = z.object({
  questionId: z.number().min(0),
  choiceIndices: z.array(z.number().min(0)),
})

const measurementSchema = z.object({
  id: z.number().min(0),
  sessionId: z.number().min(0),
  definitionId: z.number().min(0),
  hasRecording: z
    .boolean()
    .optional()
    .transform((value) => value ?? false),
  answers: z.array(measurementQuestionSchema).nullable(),
})

export type MeasurementDetails = z.infer<typeof measurementSchema>

export const measurementAnswerInputSchema = z.object({
  questionId: z.number().min(0),
  choiceIndices: z.array(z.number().min(0)),
})

export type MeasurementAnswerInput = z.infer<
  typeof measurementAnswerInputSchema
>

export interface CreateMeasurementParams {
  sessionId: number
  definitionId: number
  answers: MeasurementAnswerInput[]
  encoding: string | null
}

const createMeasurementResultSchema = z.object({
  measurementId: z.number().min(0),
})

export interface SaveMeasurementRecordingParams {
  measurementId: number
  recording:
    | {
        kind: 'blob'
        body: Blob
      }
    | {
        kind: 'fileUri'
        uri: string
        contentType: string
      }
}

export interface MeasurementRecordingUrlParams {
  measurementId: number
}

export const measurementDefinitionDetailSchema = z.object({
  id: z.number().min(0),
  name: z.string().min(1),
  sessionDefinitionId: z.number().min(0),
  audioTrack: nullableStringSchema,
})

const measurementDetailSchema = z.object({
  id: z.number().min(0),
  definitionId: z.number().min(0),
  sessionId: z.number().min(0),
  created: z.date(),
  recording: nullableStringSchema,
})

export async function fetchMeasurements() {
  const api = new MeasurementApi(getApiConfig())
  const dtos = await api.apiMeasurementGet()

  return dtos.map((dto) => measurementSummarySchema.parse(dto))
}

export async function fetchMeasurementById(id: number) {
  const api = new MeasurementApi(getApiConfig())
  const dto = await api.apiMeasurementIdGet({ id })
  return measurementSchema.parse({ id, ...dto })
}

export async function createMeasurement({
  sessionId,
  definitionId,
  answers,
  encoding,
}: CreateMeasurementParams) {
  const api = new MeasurementApi(getApiConfig())
  const measurementDTO: MeasurementDTO & { encoding: string | null } = {
    sessionId,
    definitionId,
    encoding,
    answers: answers.map((answer) => ({
      questionId: answer.questionId,
      choiceIndices: answer.choiceIndices,
    })),
  }
  const dto = await api.apiMeasurementPost({
    measurementDTO,
  })

  return createMeasurementResultSchema.parse(dto)
}

/**
 * Hand-written because `scripts/openapi-generate.mjs` removes this endpoint
 * from the temporary spec instead of generating a client from the incomplete
 * OpenAPI requestBody contract.
 */
export async function saveMeasurementRecording({
  measurementId,
  recording,
}: SaveMeasurementRecordingParams) {
  const config = getApiConfig()
  const url = `${config.basePath.replace(/\/+$/, '')}/api/measurement/${encodeURIComponent(String(measurementId))}/recording`
  const headers = new Headers(config.headers)
  const contentType =
    recording.kind === 'blob' ? recording.body.type : recording.contentType

  headers.set('Content-Type', contentType)

  if (!headers.has('Authorization') && config.accessToken) {
    const token = await config.accessToken('Bearer', [])
    if (token) {
      headers.set('Authorization', `Bearer ${token}`)
    }
  }

  if (recording.kind === 'fileUri') {
    const result = await FileSystem.uploadAsync(url, recording.uri, {
      httpMethod: 'POST',
      uploadType: FileSystem.FileSystemUploadType.BINARY_CONTENT,
      headers: Object.fromEntries(headers.entries()),
    })

    if (result.status < 200 || result.status >= 300) {
      throw new Error(`Failed to save measurement recording: ${result.status}`)
    }

    const dto = MeasurementFromJSON(JSON.parse(result.body))

    return measurementDetailSchema.parse(dto)
  }

  const response = await (config.fetchApi ?? fetch)(url, {
    method: 'POST',
    headers: Object.fromEntries(headers.entries()),
    body: recording.body,
    credentials: config.credentials,
  })

  if (!response.ok) {
    throw new Error(
      `Failed to save measurement recording: ${response.status} ${response.statusText}`,
    )
  }

  const dto = MeasurementFromJSON(await response.json())

  return measurementDetailSchema.parse(dto)
}

/**
 * Returns the playback URL for a saved measurement recording. Suitable for
 * use as a media element `src` / `uri`.
 *
 * The generated API models this endpoint as a GET with a JSON body, but media
 * players need a plain URL.
 */
export function buildMeasurementRecordingUrl({
  measurementId,
}: MeasurementRecordingUrlParams) {
  const { basePath } = getApiConfig()

  return `${basePath.replace(/\/+$/, '')}/api/measurement/${encodeURIComponent(String(measurementId))}/recording`
}

export function buildMeasurementRecordingAudioSource({
  measurementId,
}: MeasurementRecordingUrlParams): AudioSource {
  const config = getApiConfig()
  const headers = config.headers

  return {
    uri: buildMeasurementRecordingUrl({ measurementId }),
    ...(headers != null ? { headers } : null),
  }
}
